// frontend/src/pages/OAuthCallback.jsx

import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { FileText } from 'lucide-react'
import toast from 'react-hot-toast'

export const OAuthCallback = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { handleGoogleOAuthCallback, handleGitHubOAuthCallback, handleYandexOAuthCallback } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Защита от двойного вызова (React StrictMode вызывает useEffect дважды,
  // а OAuth код одноразовый - повторный обмен падает с ошибкой)
  const callbackProcessed = useRef(false)

  useEffect(() => {
    // Предотвращаем повторную обработку одноразового кода
    if (callbackProcessed.current) {
      return
    }
    callbackProcessed.current = true

    const handleCallback = async () => {
      try {
        const code = searchParams.get('code')
        const state = searchParams.get('state')
        const oauthError = searchParams.get('error')

        // Google может вернуть error вместо code (пользователь отменил и т.п.)
        if (oauthError) {
          throw new Error(`OAuth провайдер вернул ошибку: ${oauthError}`)
        }
        
        // Определяем провайдера из URL path
        const path = window.location.pathname
        let provider = 'google'
        if (path.includes('/github')) {
          provider = 'github'
        } else if (path.includes('/yandex')) {
          provider = 'yandex'
        } else if (path.includes('/google')) {
          provider = 'google'
        }

        if (!code) {
          throw new Error('No authorization code received')
        }

        let result = null
        if (provider === 'google') {
          result = await handleGoogleOAuthCallback(code, state)
        } else if (provider === 'github') {
          result = await handleGitHubOAuthCallback(code, state)
        } else if (provider === 'yandex') {
          result = await handleYandexOAuthCallback(code, state)
        } else {
          throw new Error(`Unknown provider: ${provider}`)
        }

        // useAuth проглатывает ошибки и возвращает undefined -
        // проверяем что авторизация действительно прошла
        if (!result || !result.access_token) {
          throw new Error('Авторизация не удалась. Попробуйте ещё раз.')
        }

        // Получаем URL для возврата или идем на projects
        const returnUrl = sessionStorage.getItem('oauth_return_url') || '/projects'
        sessionStorage.removeItem('oauth_return_url')

        navigate(returnUrl)
      } catch (err) {
        console.error('OAuth callback error:', err)
        setError(err.message || 'Ошибка при обработке OAuth callback')
        toast.error(err.message || 'Ошибка авторизации')
      } finally {
        setLoading(false)
      }
    }

    handleCallback()
  }, [searchParams, handleGoogleOAuthCallback, handleGitHubOAuthCallback, handleYandexOAuthCallback, navigate])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <FileText size={48} className="text-blue-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-4">Обработка авторизации...</h2>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <FileText size={48} className="text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-4 text-red-600">Ошибка авторизации</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Вернуться на вход
          </button>
        </div>
      </div>
    )
  }

  return null
}
